'use client'

import { LineArrowRight } from '@qr-front/icons/line-arrow-right'
import { LineDownload } from '@qr-front/icons/line-download'
import { buttonCls } from '@qr-front/ui/components/Button'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn } from '@qr-front/ui/utils'
import { isFileUploadingAtom } from 'components/FileUploadToMediaStorage'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { pushToDataLayer } from 'lib/analytics/dataLayer'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useBus, useListener } from 'react-bus'

import { getStore } from '@/store'

import { captureBuilderEvent } from '../../builderAnalytics'
import { useCurrGeneratorStep } from '../../hooks'

export const NEXT_BUTTON_CLICK_EVENT = 'GENERATOR_NEXT_BUTTON_CLICK'
export const RESET_NEXT_BUTTON_LOADING_EVENT = 'RESET_NEXT_BUTTON_LOADING'

export const ATTEMPT_NAVIGATION_TO_DESIGN_STEP_EVENT =
	'NAVIGATION_TO_DESIGN_STEP_ATTEMPTED'

export const SUBMIT_DESIGN_FORM_EVENT = 'SUBMIT_DESIGN_FORM'

export const isSubmittingAtom = atom(false)
export const isLoadingAtom = atom(false)

export const triggerNextStep = (bus: ReturnType<typeof useBus>) => {
	const store = getStore()
	store.set(isLoadingAtom, true)
	store.set(isSubmittingAtom, true)
	bus.emit(NEXT_BUTTON_CLICK_EVENT)
}

export const NextButton = ({
	editMode,
	isChangeTypeMode = false,
	className,
}: {
	editMode: boolean
	isChangeTypeMode?: boolean
	className?: string
}) => {
	const t = useTranslations()
	const bus = useBus()

	const currStep = useCurrGeneratorStep()
	const isDesignStep = currStep === 'design'
	const [isLoading, setIsLoading] = useAtom(isLoadingAtom)
	const isFileUploading = useAtomValue(isFileUploadingAtom)
	const setIsSubmitting = useSetAtom(isSubmittingAtom)

	// Reset loading state on step change for better UX
	useEffect(() => {
		setIsLoading(false)
		setIsSubmitting(false)
	}, [currStep, setIsSubmitting, setIsLoading])

	const handleClick = async () => {
		// MYQRCODE-10778 AC#13: INTENT ONLY — the button opens the signup modal
		// or routes to checkout/dashboard, it does not itself download a file.
		if (!editMode && isDesignStep) {
			captureBuilderEvent('builder_click_download', {
				section: 'wizard_stepper',
			})
			pushToDataLayer({ event: 'download_qr_click' })
		}
		triggerNextStep(bus)
	}

	useListener(RESET_NEXT_BUTTON_LOADING_EVENT, () => {
		setIsLoading(false)
		setIsSubmitting(false)
	})

	if (currStep === 'type') {
		return null
	}

	const isCreateDesignStep = !editMode && isDesignStep

	const designStepLabel =
		editMode || isChangeTypeMode
			? t('generator__footer__edit_mode__complete_button')
			: t('generator__footer__download_qr_button')

	const icon = isCreateDesignStep ? (
		<LineDownload className="size-4 min-w-4" />
	) : editMode && isDesignStep ? null : (
		<LineArrowRight className="size-4 min-w-4" />
	)

	const loading = isLoading || isFileUploading

	return (
		<button
			disabled={loading}
			onClick={handleClick}
			className={cn(
				buttonCls({ size: 'large', pulsate: !loading }),
				'w-full min-w-0 desktop:w-auto',
				className,
			)}
		>
			<span>
				{currStep === 'design'
					? designStepLabel
					: t('generator__footer__next_button')}
			</span>
			{isLoading ? (
				<Spinner color="white" className="size-4 min-w-4" />
			) : (
				icon
			)}
		</button>
	)
}
